import { useCallback, useMemo, useRef } from 'react';

interface UseCreationNavigationProps<T> {
  items?: T[];
  page: number;
  totalPages: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onCreationSelect: (creation: T) => void;
}

export function useCreationNavigation<T extends { id?: string }>({
  items,
  page,
  totalPages,
  loading = false,
  onPageChange,
  onCreationSelect,
}: UseCreationNavigationProps<T>) {
  const currentIndex = useRef<number>(-1);

  // 点击选择的creation
  const handleSelectedCreation = useCallback(
    (creationId: string) => {
      if (!items) return;
      const index = items.findIndex((item) => item.id === creationId);
      if (index !== -1) {
        currentIndex.current = index;
        onCreationSelect(items[index]);
      }
    },
    [items],
  );

  // 分页
  const handleNavigation = useCallback(
    (type: 'prev' | 'next') => {
      if (!items || loading) return null;

      const isFirst = currentIndex.current === 0;
      const isLast = currentIndex.current === items.length - 1;

      // 需要翻页的情况
      if (isFirst && type === 'prev' && page > 1) {
        onPageChange(page - 1);
        return 'prev';
      }

      if (isLast && type === 'next' && page < totalPages) {
        onPageChange(page + 1);
        return 'next';
      }

      // 同页导航
      const newIndex = currentIndex.current + (type === 'prev' ? -1 : 1);
      if (newIndex >= 0 && newIndex < items.length) {
        currentIndex.current = newIndex;
        onCreationSelect(items[newIndex]);
      }
      return null;
    },
    [items, loading, page, totalPages],
  );

  // 按钮状态
  const isPrevButtonDisabled = useMemo(
    () => (currentIndex.current === 0 && page === 1) || loading,
    [page, loading, currentIndex.current],
  );

  const isNextButtonDisabled = useMemo(
    () => (currentIndex.current === (items?.length ?? 0) - 1 && page === totalPages) || loading,
    [items?.length, page, totalPages, loading, currentIndex.current],
  );

  // 处理页面切换后的状态
  const handlePageChange = (direction: 'prev' | 'next' | null, newItems?: T[]) => {
    if (!newItems || !direction) return;

    const newIndex = direction === 'prev' ? newItems.length - 1 : 0;
    currentIndex.current = newIndex;
    onCreationSelect(newItems[newIndex]);
  };

  return {
    currentIndex: currentIndex.current,
    isPrevButtonDisabled,
    isNextButtonDisabled,
    handleSelectedCreation,
    handleNavigation,
    handlePageChange,
  };
}
